import React, { useState } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";

import { usePageTracker } from "../../../utils/googleAnalytics";
import {
  canViewAdvancedInsights,
  canViewMyInsightsForInterviewer,
  canViewOrgInsights,
} from "../../../utils/permissions";
import {
  AtsDataState,
  AtsDataStateQueryResult,
  FeatureName,
  MetricName,
  useAtsDataStateQuery,
} from "../../graphql";
import useFeatureFlag from "../../graphql/hooks/useFeatureFlag";
import useCurrentUser from "../../hooks/useCurrentUser";
import { useHasFeatureAccess } from "../../hooks/useHasFeatureAccess";
import {
  AnalyticsPageLayout,
  AnalyticsSidebarType,
} from "./AnalyticsPageLayout";
import useEnabledMetrics from "./useEnabledMetrics";

const AnalyticsPage: React.FC = () => {
  const atsDataState = useAtsDataStateQuery();

  if (atsDataState.loading) {
    return null;
  }

  return <AnalyticsPageInner atsDataState={atsDataState} />;
};

const AnalyticsPageInner: React.FC<{
  atsDataState: AtsDataStateQueryResult;
}> = ({ atsDataState }) => {
  usePageTracker("analytics");
  const currentUser = useCurrentUser();

  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    currentUser.organization.id
  );
  const [showBHDemoDashboard, setShowBHDemoDashboard] = useState<boolean>(true);

  const { metric: metricParam, interviewerId: interviewerIdParam } = useParams<{
    metric: string;
    interviewerId: string;
  }>();
  const metric = metricParam?.toUpperCase() as MetricName;

  const enabledMetrics = useEnabledMetrics(
    atsDataState.data?.atsDataState?.enabledMetrics || []
  );
  const myInsightsEnabled = !useFeatureFlag("analytics:my-insights:disabled");
  const overviewRefreshEnabled = useFeatureFlag("analytics:overview");
  const topicTrendsEnabled = useFeatureFlag("analytics:topic-trends");
  const candidateMotivationsEnabled = useFeatureFlag(
    "analytics:candidate-motivations"
  );
  const candidateQuestionsEnabled = useFeatureFlag(
    "analytics:candidate-questions"
  );
  const compensationTrendsEnabled = useFeatureFlag(
    "analytics:compensation-trends"
  );
  const skillsReportEnabled = useFeatureFlag("analytics:skills-report");
  const insightsChatEnabled = useFeatureFlag("insights:advanced-chat");
  const advancedInsightsEnabled =
    useHasFeatureAccess(FeatureName.AdvancedInsights) &&
    canViewAdvancedInsights(currentUser) &&
    (topicTrendsEnabled ||
      candidateMotivationsEnabled ||
      candidateQuestionsEnabled ||
      compensationTrendsEnabled ||
      skillsReportEnabled ||
      insightsChatEnabled);

  const location = useLocation();
  const isMyInsights = location.pathname.includes("insights/interviewer");
  const isOverview = location.pathname.includes("overview");
  const isSkillsReport = location.pathname.includes("skill-report");
  const isCustomInsights = location.pathname.includes("custom");
  const isAdvancedInsights =
    location.pathname.includes("topic-trends") ||
    location.pathname.includes("candidate-motivations") ||
    location.pathname.includes("candidate-questions") ||
    location.pathname.includes("compensation-trends") ||
    isSkillsReport;
  const sidebarEnabled = !isOverview && !isMyInsights && !isCustomInsights;

  if (useHasFeatureAccess(FeatureName.Insights) === false && !isMyInsights) {
    return <Navigate to="/" replace />;
  }

  let sidebarType: AnalyticsSidebarType = sidebarEnabled ? "default" : "none";
  if (advancedInsightsEnabled && isAdvancedInsights) {
    sidebarType = "advanced";
  }

  if (!canViewOrgInsights(currentUser)) {
    if (
      !(
        isMyInsights &&
        myInsightsEnabled &&
        canViewMyInsightsForInterviewer(currentUser, interviewerIdParam)
      )
    ) {
      return <Navigate to="/" replace />;
    }
  }

  if (metric && !enabledMetrics.includes(metric)) {
    if (canViewOrgInsights(currentUser)) {
      return <Navigate to="/insights/overview" replace />;
    }
    return <Navigate to="/" replace />;
  }

  if (isAdvancedInsights && !advancedInsightsEnabled) {
    return <Navigate to="/" replace />;
  }

  const bgColor = isOverview && !overviewRefreshEnabled ? "unset" : "white";
  const maxW = metric ? undefined : isOverview ? "1920px" : "1200px";

  return (
    <AnalyticsPageLayout
      metric={metric || MetricName.Report}
      data-tour-id="analytics-page"
      bgColor={bgColor}
      maxW={isAdvancedInsights ? "unset" : maxW}
      noPadding={isAdvancedInsights}
      sidebarType={sidebarType}
      wrapContent={!isCustomInsights}
      // TODO: pass this through context only and fetch in useEnabledMetrics
      atsDataState={atsDataState}
    >
      <Outlet
        context={{
          selectedOrgId,
          setSelectedOrgId,
          showBHDemoDashboard,
          setShowBHDemoDashboard,
          atsDataState:
            atsDataState.data?.atsDataState?.atsDataState ||
            AtsDataState.AtsNotSupported,
          atsEnabledMetrics:
            atsDataState.data?.atsDataState?.enabledMetrics || [],
        }}
      />
    </AnalyticsPageLayout>
  );
};

export default AnalyticsPage;
